import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../api/client.js";
import { useAuth } from "../../auth/AuthContext.js";

/**
 * FIX-057 owner 14:16 IL / t1749u: ONE more bar on the Review page, under the Telegram daily-publish bars, in the same pattern and style (`card auto-run-bar`,
 * headline + muted line, 15 s refresh, a 403 hides it): Instagram auto-posting per persona — On / Off, the last slot and the next slot (Israel time), and a
 * Turn off / Turn on button. The switch is stored server-side (ig_autopost_switch); IG_AUTOPOST_ENABLED on the server stays the master switch.
 * Routes (new, owner-only): `GET /re/:t/igautopost/status`, `POST /re/:t/igautopost/switch {persona, enabled}`. Nothing else on the page is touched.
 */

const REFRESH_MS = 15_000;

export interface IgSlotView { readonly at: string; readonly kind: "trial" | "regular"; readonly material: "regular" | "test"; readonly status: string; readonly postUrl: string | null; readonly error: string | null }
export interface IgPersonaStatus { readonly persona: string; readonly profile: string; readonly enabled: boolean; readonly changedAt: string | null; readonly changedBy: string | null; readonly last: IgSlotView | null; readonly next: IgSlotView | null }
export interface IgStatus { readonly master: boolean; readonly active: boolean; readonly missing: readonly string[]; readonly personas: readonly IgPersonaStatus[] }

const PERSONA_NAME: Record<string, string> = { YUNA_1: "Yuna", EMMA: "Emma" };
const RESULT: Record<string, string> = { posted: "posted", failed: "failed", skipped: "skipped", cancelled: "cancelled", scheduled: "scheduled", submitting: "uploading", planned: "planned" };

/** HH:MM Israel time, with the Israel date in front when it is not today (the video bar's rule). */
export function igWhen(iso: string, now: Date = new Date()): string {
  const day = (d: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jerusalem" }).format(d);
  const hm = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Jerusalem", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
  return `${day(new Date(iso)) === day(now) ? "" : `${day(new Date(iso))} `}${hm}`;
}
const kindText = (s: IgSlotView): string => `${s.kind === "trial" ? "Trial" : "Regular"}${s.material === "test" ? " (test)" : ""}`;

/** Pure: the bar's two lines for one persona (tested directly). */
export function igBarText(p: IgPersonaStatus, now: Date = new Date()): { readonly headline: string; readonly detail: string } {
  const name = `${PERSONA_NAME[p.persona] ?? p.persona} (${p.profile})`;
  const headline = `Instagram auto-posting · ${name} · ${p.enabled ? "On" : "Off"}`;
  const last = p.last ? `Last: ${igWhen(p.last.at, now)} IL · ${kindText(p.last)} · ${RESULT[p.last.status] ?? p.last.status}` : "Last: none yet";
  const next = p.enabled ? (p.next ? `Next: ${igWhen(p.next.at, now)} IL · ${kindText(p.next)}` : "Next: none planned") : "Next: none (switched off)";
  return { headline, detail: `${last} · ${next}` };
}

export function ReviewsIgAutopostBar({ tenantId }: { readonly tenantId: string }) {
  const { csrfToken } = useAuth();
  const [st, setSt] = useState<IgStatus | null>(null);
  const [unavailable, setUnavailable] = useState<"none" | "hidden" | "unavailable">("none");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const off = useRef(false);

  useEffect(() => {
    let live = true;
    off.current = false;
    const load = async (): Promise<void> => {
      if (off.current) return;
      try {
        const s = await api.get<IgStatus>(`/re/${tenantId}/igautopost/status`);
        if (!live || !s || typeof s !== "object") return;
        setUnavailable("none"); setSt(s);
      } catch (e) {
        if (live && e instanceof ApiError && (e.status === 403 || e.status === 404 || e.status === 503)) { off.current = true; setSt(null); setUnavailable(e.status === 403 ? "hidden" : "unavailable"); }
        else if (live) setUnavailable((u) => (u === "hidden" ? u : "unavailable"));
      }
    };
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => { live = false; clearInterval(t); };
  }, [tenantId]);

  if (unavailable === "hidden") return null;
  if (!st || !st.master || !st.active || !Array.isArray(st.personas) || st.personas.length === 0) {
    const line = !st ? (unavailable === "unavailable" ? "Instagram auto-posting status is not available" : "Checking Instagram auto-posting…") : "Instagram auto-posting · Off (server setting)";
    return (
      <div className="card auto-run-bar" data-testid="reviews-ig-bar" data-state="idle" role="status" aria-live="polite">
        <strong data-testid="reviews-ig-bar-headline">{line}</strong>
      </div>
    );
  }
  const toggle = async (p: IgPersonaStatus): Promise<void> => {
    const to = !p.enabled;
    const q = to
      ? `Turn Instagram auto-posting ON for ${PERSONA_NAME[p.persona] ?? p.persona}? Uploads start again from the next slot.`
      : `Turn Instagram auto-posting OFF for ${PERSONA_NAME[p.persona] ?? p.persona}? No new uploads; uploads already scheduled and not yet published are cancelled.`;
    if (!window.confirm(q)) return;
    setBusy(true); setMsg(null);
    try {
      const s = await api.post<IgStatus>(`/re/${tenantId}/igautopost/switch`, csrfToken, { persona: p.persona, enabled: to });
      if (s) setSt(s);
      setMsg(to ? "Switched on." : "Switched off.");
    } catch (e) { const b = e instanceof ApiError ? (e.body as { message?: string } | null) : null; setMsg(b?.message ?? "The switch failed."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="reviews-ig-bar" data-state="ready">
      {st.personas.map((p) => {
        const t = igBarText(p);
        return (
          <div key={p.persona} className="card auto-run-bar" data-testid={`reviews-ig-bar-${p.persona}`} data-state={p.enabled ? "on" : "off"} role="status" aria-live="polite">
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <strong data-testid={`reviews-ig-bar-headline-${p.persona}`}>{t.headline}</strong>
              <span className="muted" data-testid={`reviews-ig-bar-detail-${p.persona}`}>{t.detail}</span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button type="button" className={p.enabled ? "danger" : undefined} data-testid={`reviews-ig-bar-toggle-${p.persona}`} disabled={busy} onClick={() => void toggle(p)}>{p.enabled ? "Turn off" : "Turn on"}</button>
              </span>
            </div>
            {msg ? <p className="muted" style={{ margin: 0 }}>{msg}</p> : null}
          </div>
        );
      })}
    </div>
  );
}
